import {
    all,
    call,
    cancel,
    fork,
    put,
    race,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';
import axios from 'axios';
import keyBy from 'lodash.keyby';
import { push } from 'connected-react-router';

// Types
import { SagaIterator } from 'redux-saga';
import {
    RequestLoadTimesheetAction,
    RequestCreateTimesheetAction
} from 'services/timesheet/types';

// Utils
// @ts-ignore
import axiosPoll from 'utils/axiosPoll';
import { formatDataForPostRequest } from 'utils/formatter/data-format';

// Constants
import {
    TIMESHEET_LOAD_REQUESTED,
    TIMESHEET_LOAD_CANCELED,
    TIMESHEET_CREATE_REQUESTED
    // @ts-ignore
} from './constants';

// Actions
// @ts-ignore
import { updateQueryString } from '../router/actions';
// @ts-ignore
import { setTimesheetTimesheetEntries } from '../timesheetTimesheetEntries/actions';
import {
    succeededLoadTimesheet,
    failedLoadTimesheet,
    succeededCreateTimesheet,
    failedCreateTimesheet,
    setTimesheet
    // @ts-ignore
} from './actions';

// Selectors
// @ts-ignore
import { idTokenSelector } from '../auth/selectors';
// @ts-ignore
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* loadTimesheetSucceeded(response: any): SagaIterator {
    // HACK: by doing this errors like 404 don't exist
    const { timesheetEntries, ...timesheet } = response.data[0];

    const timesheetEntriesKeyedByUuid = keyBy(timesheetEntries, 'uuid');

    // Set Timesheet Timesheet Entries
    yield put(setTimesheetTimesheetEntries(timesheetEntriesKeyedByUuid));

    // Set Timesheet
    yield put(setTimesheet(timesheet));

    // Succeeded Load Timesheet
    yield put(succeededLoadTimesheet(response));
}

export function* loadTimesheetFailed(error: Error): SagaIterator {
    yield put(failedLoadTimesheet(error));
}

export function* loadTimesheet(
    action: RequestLoadTimesheetAction
): SagaIterator {
    const {
        payload: { timesheetId }
    } = action;

    // Select organization ID
    const organizationId = yield select(selectCurrentOrganizationId);

    yield call(axiosPoll, {
        config: {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/Timesheets`,
            params: {
                organizationId,
                filter: {
                    eager: 'timesheetEntries',
                    require: {
                        id: timesheetId
                    },
                    where: {
                        'timesheetEntries.deletedAt': { $exists: false },
                        'timesheetEntries.versionedAt': { $exists: false }
                    }
                }
            }
        },
        succeeded: loadTimesheetSucceeded,
        failed: loadTimesheetFailed
    });
}

export function* watchTimesheetLoadRequested(): SagaIterator {
    let lastTask;
    while (true) {
        const [requested] = yield race([
            take(TIMESHEET_LOAD_REQUESTED),
            take(TIMESHEET_LOAD_CANCELED)
        ]);

        if (lastTask) {
            yield cancel(lastTask);
        }
        if (requested) {
            lastTask = yield fork(loadTimesheet, requested);
        }
    }
}

export function* createTimesheet(
    action: RequestCreateTimesheetAction
): SagaIterator {
    const {
        payload: { data }
    } = action;

    // Select ID Token
    const idToken = yield select(idTokenSelector);

    // Select organization ID
    const organizationId = yield select(selectCurrentOrganizationId);

    try {
        // @ts-ignore
        const response = yield call(axios, {
            method: 'post',
            url: `${REACT_APP_API_URL}/v1/Timesheets`,
            headers: {
                Authorization: idToken
            },
            data: {
                ...formatDataForPostRequest(data),
                organizationId
            }
        });

        const { timesheetEntries, ...timesheet } = response.data;
        const timesheetEntriesKeyedByUuid = keyBy(timesheetEntries, 'uuid');

        // Set Timesheet
        yield put(setTimesheet(timesheet));

        // Set Timesheet Timesheet Entries
        yield put(setTimesheetTimesheetEntries(timesheetEntriesKeyedByUuid));

        // Succeeded Create Timesheet
        yield put(succeededCreateTimesheet(response));

        // Update Query String
        yield put(
            updateQueryString({
                createTimesheetModal: undefined,
                individualTimesheetModal: undefined,
                groupTimesheetModal: undefined
            })
        );

        // Update route
        yield put(push(`/organization/time/timesheets/${timesheet.id}`));
    } catch (error) {
        yield put(failedCreateTimesheet(error));
    }
}

export function* watchTimesheetCreateRequested(): SagaIterator {
    yield takeLatest(TIMESHEET_CREATE_REQUESTED, createTimesheet);
}

export default function* timesheetSagas(): SagaIterator {
    yield all([
        fork(watchTimesheetLoadRequested),
        fork(watchTimesheetCreateRequested)
    ]);
}
